import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import adminApiService from '../../services/adminApiService';
import toast from 'react-hot-toast';
import {
  ChartPieIcon,
  ChartBarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  UsersIcon,
  BriefcaseIcon,
  AcademicCapIcon,
  CurrencyDollarIcon,
  GlobeAltIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon
} from '@heroicons/react/24/outline';

interface AnalyticsData {
  userGrowth: {
    month: string;
    users: number;
    students: number;
    employers: number;
  }[];
  jobMetrics: {
    totalPosted: number;
    totalApplied: number;
    averageApplications: number;
    topCategories: string[];
    successRate: number;
  };
  courseAnalytics: {
    totalEnrollments: number;
    completionRate: number;
    averageScore: number;
    topCourses: string[];
    aiValidationAccuracy: number;
  };
  subscriptionMetrics: {
    mrr: number;
    churnRate: number;
    upgrades: number;
    downgrades: number;
    tierDistribution: { basic: number; premium: number; enterprise: number };
  };
  verificationMetrics: {
    identityProcessed: number;
    businessProcessed: number;
    successRate: number;
    averageProcessingTime: number;
    flaggedProfiles: number;
  };
  aiSystemMetrics: {
    cvGenerated: number;
    personalityAnalysis: number;
    backgroundChecks: number;
    reliabilityScores: number;
    averageAccuracy: number;
  };
  socialMediaAnalytics: {
    connectedProfiles: number;
    backgroundChecksRun: number;
    flaggedContent: number;
    personalityInsights: number;
  };
  rankingSystemMetrics: {
    totalRanked: number;
    publicProfiles: number;
    globalRankings: number;
    skillsAssessed: number;
    longitudinalTracking: number;
  };
  deviceAnalytics: {
    desktop: number;
    mobile: number;
    tablet: number;
  };
  geographicData: {
    country: string;
    users: number;
    revenue: number;
  }[];
}

const Analytics: React.FC = () => {
  const { adminUser, hasPermission } = useAdmin();
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<string>('30d');
  const [selectedMetric, setSelectedMetric] = useState<string>('overview');

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      
      const response = await adminApiService.getAnalytics(timeRange);
      if (response.success && response.data) {
        // Transform backend data to frontend format
        const backendAnalytics = response.data;
        
        const transformedAnalytics: AnalyticsData = {
          userGrowth: backendAnalytics.userGrowth || [],
          jobMetrics: backendAnalytics.jobMetrics || {
            totalPosted: 0,
            totalApplied: 0,
            averageApplications: 0,
            topCategories: [],
            successRate: 0
          },
          courseAnalytics: backendAnalytics.courseAnalytics || {
            totalEnrollments: 0,
            completionRate: 0,
            averageScore: 0,
            topCourses: [],
            aiValidationAccuracy: 0
          },
          subscriptionMetrics: backendAnalytics.subscriptionMetrics || {
            mrr: 0,
            churnRate: 0,
            upgrades: 0,
            downgrades: 0,
            tierDistribution: { basic: 0, premium: 0, enterprise: 0 }
          },
          verificationMetrics: backendAnalytics.verificationMetrics || {
            identityProcessed: 0,
            businessProcessed: 0,
            successRate: 0,
            averageProcessingTime: 0,
            flaggedProfiles: 0
          },
          aiSystemMetrics: backendAnalytics.aiSystemMetrics || {
            cvGenerated: 0,
            personalityAnalysis: 0,
            backgroundChecks: 0,
            reliabilityScores: 0,
            averageAccuracy: 0
          },
          socialMediaAnalytics: backendAnalytics.socialMediaAnalytics || {
            connectedProfiles: 0,
            backgroundChecksRun: 0,
            flaggedContent: 0,
            personalityInsights: 0
          },
          rankingSystemMetrics: backendAnalytics.rankingSystemMetrics || {
            totalRanked: 0,
            publicProfiles: 0,
            globalRankings: 0,
            skillsAssessed: 0,
            longitudinalTracking: 0
          },
          // These would need additional backend implementation
          deviceAnalytics: {
            desktop: Math.floor(backendAnalytics.summary?.totalUsers * 0.6) || 0,
            mobile: Math.floor(backendAnalytics.summary?.totalUsers * 0.35) || 0,
            tablet: Math.floor(backendAnalytics.summary?.totalUsers * 0.05) || 0
          },
          geographicData: [
            { country: 'United States', users: Math.floor(backendAnalytics.summary?.totalUsers * 0.4) || 0, revenue: backendAnalytics.subscriptionMetrics?.mrr * 0.4 || 0 },
            { country: 'Canada', users: Math.floor(backendAnalytics.summary?.totalUsers * 0.15) || 0, revenue: backendAnalytics.subscriptionMetrics?.mrr * 0.15 || 0 },
            { country: 'United Kingdom', users: Math.floor(backendAnalytics.summary?.totalUsers * 0.12) || 0, revenue: backendAnalytics.subscriptionMetrics?.mrr * 0.12 || 0 },
            { country: 'Australia', users: Math.floor(backendAnalytics.summary?.totalUsers * 0.08) || 0, revenue: backendAnalytics.subscriptionMetrics?.mrr * 0.08 || 0 },
            { country: 'Germany', users: Math.floor(backendAnalytics.summary?.totalUsers * 0.06) || 0, revenue: backendAnalytics.subscriptionMetrics?.mrr * 0.06 || 0 }
          ]
        };

        setAnalytics(transformedAnalytics);
      } else {
        throw new Error('Failed to load analytics data');
      }
    } catch (error: any) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <ChartPieIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No analytics data available</h3>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Platform Analytics</h1>
          <p className="mt-2 text-sm text-gray-700">
            Comprehensive insights into platform performance and user behavior
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Monthly Recurring Revenue</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {formatCurrency(analytics.subscriptionMetrics.mrr)}
                    </div>
                    <div className="ml-2 flex items-baseline text-sm font-semibold text-green-600">
                      <ArrowUpIcon className="self-center flex-shrink-0 h-4 w-4 text-green-500" />
                      <span className="sr-only"> Increased by </span>
                      12.3%
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UsersIcon className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Users</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {analytics.userGrowth[analytics.userGrowth.length - 1]?.users.toLocaleString()}
                    </div>
                    <div className="ml-2 flex items-baseline text-sm font-semibold text-green-600">
                      <ArrowUpIcon className="self-center flex-shrink-0 h-4 w-4 text-green-500" />
                      8.1%
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BriefcaseIcon className="h-6 w-6 text-purple-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Job Success Rate</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {formatPercentage(analytics.jobMetrics.successRate)}
                    </div>
                    <div className="ml-2 flex items-baseline text-sm font-semibold text-red-600">
                      <ArrowDownIcon className="self-center flex-shrink-0 h-4 w-4 text-red-500" />
                      2.1%
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <AcademicCapIcon className="h-6 w-6 text-orange-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Course Completion</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {formatPercentage(analytics.courseAnalytics.completionRate)}
                    </div>
                    <div className="ml-2 flex items-baseline text-sm font-semibold text-green-600">
                      <ArrowUpIcon className="self-center flex-shrink-0 h-4 w-4 text-green-500" />
                      5.4%
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* User Growth Chart */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">User Growth Trend</h3>
          <div className="mt-5">
            <div className="flex items-end space-x-2 h-64">
              {analytics.userGrowth.map((data, index) => (
                <div key={data.month} className="flex flex-col items-center flex-1">
                  <div className="w-full bg-gray-100 rounded-t">
                    <div 
                      className="bg-blue-500 rounded-t"
                      style={{ 
                        height: `${(data.users / Math.max(...analytics.userGrowth.map(d => d.users))) * 200}px`,
                        minHeight: '10px'
                      }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-500 mt-2">{data.month}</div>
                  <div className="text-xs font-medium text-gray-900">{data.users}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {/* Job Metrics */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Job Platform Metrics</h3>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Total Jobs Posted</dt>
                <dd className="text-sm font-medium text-gray-900">{analytics.jobMetrics.totalPosted.toLocaleString()}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Total Applications</dt>
                <dd className="text-sm font-medium text-gray-900">{analytics.jobMetrics.totalApplied.toLocaleString()}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Avg Applications/Job</dt>
                <dd className="text-sm font-medium text-gray-900">{analytics.jobMetrics.averageApplications}</dd>
              </div>
            </dl>
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Top Categories</h4>
              <div className="space-y-1">
                {analytics.jobMetrics.topCategories.map((category, index) => (
                  <div key={category} className="flex items-center">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                    <span className="text-sm text-gray-600">{category}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* AI System Performance */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">AI System Performance</h3>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">CVs Generated</dt>
                <dd className="text-sm font-medium text-gray-900">{analytics.aiSystemMetrics.cvGenerated.toLocaleString()}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Personality Analysis</dt>
                <dd className="text-sm font-medium text-gray-900">{analytics.aiSystemMetrics.personalityAnalysis.toLocaleString()}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Background Checks</dt>
                <dd className="text-sm font-medium text-gray-900">{analytics.aiSystemMetrics.backgroundChecks.toLocaleString()}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Average Accuracy</dt>
                <dd className="text-sm font-medium text-gray-900">{formatPercentage(analytics.aiSystemMetrics.averageAccuracy)}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Subscription Analytics */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Subscription Metrics</h3>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Churn Rate</dt>
                <dd className="text-sm font-medium text-gray-900">{formatPercentage(analytics.subscriptionMetrics.churnRate)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Upgrades</dt>
                <dd className="text-sm font-medium text-green-600">+{analytics.subscriptionMetrics.upgrades}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Downgrades</dt>
                <dd className="text-sm font-medium text-red-600">-{analytics.subscriptionMetrics.downgrades}</dd>
              </div>
            </dl>
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Tier Distribution</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Basic</span>
                  <span className="text-sm font-medium text-gray-900">{analytics.subscriptionMetrics.tierDistribution.basic}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Premium</span>
                  <span className="text-sm font-medium text-gray-900">{analytics.subscriptionMetrics.tierDistribution.premium}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Enterprise</span>
                  <span className="text-sm font-medium text-gray-900">{analytics.subscriptionMetrics.tierDistribution.enterprise}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Device & Geographic Analytics */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        {/* Device Analytics */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Device Usage</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <ComputerDesktopIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-600">Desktop</span>
                </div>
                <div className="flex items-center">
                  <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full" 
                      style={{ width: `${(analytics.deviceAnalytics.desktop / (analytics.deviceAnalytics.desktop + analytics.deviceAnalytics.mobile + analytics.deviceAnalytics.tablet)) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{analytics.deviceAnalytics.desktop}</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <DevicePhoneMobileIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-600">Mobile</span>
                </div>
                <div className="flex items-center">
                  <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full" 
                      style={{ width: `${(analytics.deviceAnalytics.mobile / (analytics.deviceAnalytics.desktop + analytics.deviceAnalytics.mobile + analytics.deviceAnalytics.tablet)) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{analytics.deviceAnalytics.mobile}</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <ComputerDesktopIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-600">Tablet</span>
                </div>
                <div className="flex items-center">
                  <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                    <div 
                      className="bg-purple-500 h-2 rounded-full" 
                      style={{ width: `${(analytics.deviceAnalytics.tablet / (analytics.deviceAnalytics.desktop + analytics.deviceAnalytics.mobile + analytics.deviceAnalytics.tablet)) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{analytics.deviceAnalytics.tablet}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Geographic Data */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Top Countries</h3>
            <div className="space-y-3">
              {analytics.geographicData.map((country, index) => (
                <div key={country.country} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <GlobeAltIcon className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600">{country.country}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">{country.users.toLocaleString()} users</div>
                    <div className="text-xs text-gray-500">{formatCurrency(country.revenue)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;